import type { QueryClient } from '@tanstack/react-query';
import type { PageData } from 'src/ssr/organizationLandingData';
import { isSameOrgSlug } from 'src/utils/organization.util';

/**
 * Seed the org + season the SSR shell already carries into the React Query
 * cache, so the landing page doesn't re-fetch them in series after the bundle
 * parses. Both were previously fetched server-side for OG metadata and thrown
 * away (TODO(NPT-1072)); this is the consumption half.
 *
 * Call exactly once per document, from the /c/:organizationSlug page module,
 * before the tree renders. Not from an effect, and never keyed on the route:
 * `<App/>` is shared by every Vike page and `useData()` keeps returning this
 * page's payload after react-router navigates away, so re-running would stamp
 * a stale snapshot with a fresh timestamp. Cross-org poisoning is impossible
 * regardless — both keys embed the slug, which is frozen at document load.
 *
 * Values are shaped `{ data: … }` because the query functions return the raw
 * AxiosResponse and every consumer reads `.data` off it.
 */
export function seedLandingQueries(
   queryClient: QueryClient,
   routeOrgSlug: string | undefined,
   pageData: PageData | undefined | null,
): void {
   if (typeof window === 'undefined') return;
   const organization = pageData?.organization;
   if (!routeOrgSlug || !organization) return;
   // The payload and the URL must describe the same org before we trust either.
   // Case-insensitively — partner links carry title-cased slugs and the API
   // answers them, so `/c/Badger-Connect` is this org, not a different one
   // (NPT-1717).
   if (!isSameOrgSlug(organization.slug, routeOrgSlug)) return;

   // Keyed by the URL's spelling, because OrganizationGuard queries with the
   // raw route param.
   const orgKey = ['getOrganizationDetailBySlug', routeOrgSlug];
   // Never clobber an in-flight or already-resolved fetch.
   if (queryClient.getQueryState(orgKey) === undefined) {
      queryClient.setQueryData(orgKey, { data: organization });
   }

   if (pageData?.season && pageData.seasonProgramSlug) {
      // Keyed by the payload's spelling, because both season consumers
      // (landing/index.tsx and useLandingSeason) query with organization.slug.
      const seasonKey = ['travelerSeason', organization.slug, pageData.seasonProgramSlug];
      if (queryClient.getQueryState(seasonKey) === undefined) {
         queryClient.setQueryData(seasonKey, { data: pageData.season });
      }
   }
}
